import { Interval, IntervalOccasion } from '../../../../models/Interval';
import { DebitType } from '../../../../types/DebitType';
import { DebitTypeTranslationForCalculation, unitsAtUnitPrice } from './intervalClockHelper';
import { getPeriodsBetweenOccurrences } from './intervalFrequencyHelper';
import { IntervalTypeTranslationForCalculation } from './intervalTypeHelper';

/**
 * Formats a numeric string as a Swedish-locale number string.
 * Returns '0' for non-numeric or empty inputs to avoid displaying 'NaN'.
 *
 * @example setLocalString('12500') → '12 500' (space-separated thousands in sv-SE)
 */
export const setLocalString = (value: string): string => {
  if (value && !Number(value)) {
    return '0';
  }

  return Number(value).toLocaleString('sv-SE');
};

type IntervalSumInput = Pick<
  Interval,
  | 'debitType'
  | 'intervalType'
  | 'frequency'
  | 'weekParity'
  | 'occasions'
  | 'priceAfterDiscount'
  | 'globalTransportCostPriceAfterDiscount'
>;

/**
 * What one occurrence of an occasion costs:
 *   work hours × price + transport hours × transport price + transport cost + parking cost × work hours
 */
const getOccasionSum = (
  occasion: IntervalOccasion,
  priceAfterDiscount: number,
  transportCostPriceAfterDiscount: number
): number => {
  const workTime = unitsAtUnitPrice(occasion.numberOfUnitsAtUnitPrice);
  const transportTime = (occasion.transportMinutesAtUnitPrice ?? 0) / 60;
  const transportCost = Number(occasion.transportCostAtUnitPrice || 0);
  // Parking is charged per unit of work
  const parkingCost = Number(occasion.parkingCostAtUnitPrice || 0) * workTime;

  return (
    workTime * priceAfterDiscount +
    transportTime * transportCostPriceAfterDiscount +
    transportCost +
    parkingCost
  );
};

/**
 * How many times an occasion runs per occurrence of the interval. A weekly occasion runs on
 * each weekday chosen for it - the backend schedules one task per day - and at least once,
 * so the sum is not 0 before the days are chosen.
 */
const getRunsPerOccurrence = (
  occasion: IntervalOccasion,
  intervalType: Interval['intervalType']
): number => {
  if (intervalType !== 'weekly') {
    return 1;
  }

  const chosenDays = (occasion.events || []).filter((event) => event.day).length;

  return Math.max(1, chosenDays);
};

/**
 * Occurrences of the interval type per year, and billing periods of the debit type per year.
 * Veckopris counts a day as exactly a seventh of a week, so a daily Veckopris interval returns
 * 7 and 1 instead: not per-year values, but the same 7 occurrences per period.
 */
const getOccurrencesAndPeriodsPerYear = (
  intervalType: Interval['intervalType'],
  debitType: DebitType
): { occurrencesPerYear: number; periodsPerYear: number } => {
  if (debitType === DebitType.Weekly && intervalType === 'daily') {
    return { occurrencesPerYear: 7, periodsPerYear: 1 };
  }

  return {
    occurrencesPerYear: IntervalTypeTranslationForCalculation(intervalType),
    periodsPerYear: DebitTypeTranslationForCalculation[debitType],
  };
};

/**
 * Rounds a sum to whole kronor, the way it would round if calculated exactly. A time with
 * minutes, such as 1:40 (1.666... hours), is not exact in floating point, so a sum that is
 * exactly 1 462.5 can come out as 1 462.4999999999998 and round down. Rounding to 9 decimals
 * first removes that error, which is far smaller than any real difference from .5.
 */
const roundToKronor = (sum: number): number =>
  Number.isFinite(sum) ? Math.round(Number(sum.toFixed(9))) : 0;

/**
 * The interval's Summa, stored as `sumOfYieldOfPeriod`.
 *
 * - À pris: what the active occasion costs per occurrence, since each scheduled task is
 *   invoiced on its own
 * - Engångspris: what all occasions cost once, rounded
 * - Every other debit type: what all occasions cost per billing period, rounded. Each
 *   occasion counts once per chosen weekday on a weekly interval, and the total is spread
 *   over the frequency (a week-parity interval counts as every other week)
 *
 * @param interval - The interval to calculate from
 * @param activeOccasionIndex - The occasion tab shown, used by À pris
 * @returns The sum in SEK, or 0 while the interval is incomplete
 */
export const calculateIntervalSum = (
  interval: IntervalSumInput,
  activeOccasionIndex = 0
): number => {
  const occasions = interval.occasions || [];
  const priceAfterDiscount = Number(interval.priceAfterDiscount) || 0;
  const transportCostPriceAfterDiscount =
    Number(interval.globalTransportCostPriceAfterDiscount) || 0;
  const debitType = interval.debitType as DebitType;

  if (debitType === DebitType.Unit) {
    const occasion = occasions[activeOccasionIndex] ?? occasions[0];
    const sum = occasion
      ? getOccasionSum(occasion, priceAfterDiscount, transportCostPriceAfterDiscount)
      : 0;

    return Number.isFinite(sum) ? sum : 0;
  }

  const sumPerOccurrence = occasions.reduce(
    (total, occasion) =>
      total +
      getOccasionSum(occasion, priceAfterDiscount, transportCostPriceAfterDiscount) *
        getRunsPerOccurrence(occasion, interval.intervalType),
    0
  );

  if (debitType === DebitType.OneTime) {
    return roundToKronor(sumPerOccurrence);
  }

  const { occurrencesPerYear, periodsPerYear } = getOccurrencesAndPeriodsPerYear(
    interval.intervalType,
    debitType
  );
  // No frequency chosen yet divides by zero, which roundToKronor turns into 0
  return roundToKronor(
    (sumPerOccurrence * occurrencesPerYear) /
      getPeriodsBetweenOccurrences(interval) /
      periodsPerYear
  );
};
